import { Box, Button, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import moment from 'moment';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Checkbox, Chip, FormControl, InputLabel, ListItemText, OutlinedInput } from "@mui/material";
import { MoreVertOutlined } from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FlightService from "../../services/FlightService";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ShopifyPilotJobSettings from "./posSpecific/ShopifyPilotJobSettings";
import LightSpeedRJobSettings from "./posSpecific/LightSpeedRJobSettings";

const timezoneOptions = [
	'America/New_York',
	'America/Chicago',
	'America/Denver',
	'America/Los_Angeles',
	'America/Anchorage',
	'Pacific/Honolulu',
];

const normalizeTimeOfDay = (value) => {
	if (value == null || value === '' || value === 0 || value === '0') {
		return '';
	}
	if (typeof value === 'string') {
		const trimmed = value.trim();
		const hhmmss = trimmed.match(/^(\d{2}:\d{2}:\d{2})/);
		if (hhmmss) {
			return hhmmss[1];
		}
	}
	return value;
};

// POS-specific option components
const LightspeedRunOptions = ({
	historySelected,
	sinceLastRun,
	setSinceLastRun,
	loadFullInventory,
	setLoadFullInventory,
	skipInventory,
	setSkipInventory,
	onOrderOnly,
	setOnOrderOnly,
}) => {
	const safeSetSinceLastRun = typeof setSinceLastRun === 'function' ? setSinceLastRun : () => {};
	const safeSetLoadFullInventory = typeof setLoadFullInventory === 'function' ? setLoadFullInventory : () => {};
	const safeSetSkipInventory = typeof setSkipInventory === 'function' ? setSkipInventory : () => {};
	const safeSetOnOrderOnly = typeof setOnOrderOnly === 'function' ? setOnOrderOnly : () => {};

	return (
		<>
			<FormControlLabel
				sx={{ ml: 1 }}
				control={
					<Checkbox
						checked={!!sinceLastRun}
						onChange={(e) => safeSetSinceLastRun(e.target.checked)}
						color="primary"
						disabled={!!historySelected}
					/>
				}
				label="Data since last run only?"
			/>
			<FormControlLabel
				sx={{ ml: 1 }}
				control={
					<Checkbox
						checked={!!loadFullInventory}
						disabled={!!historySelected}
						onChange={(e) => safeSetLoadFullInventory(e.target.checked)}
						color="primary"
					/>
				}
				label="Load full inventory? (only for first time loads)"
			/>
			<FormControlLabel
				sx={{ ml: 1 }}
				control={
					<Checkbox
						disabled={!!historySelected}
						checked={!!skipInventory}
						onChange={(e) => safeSetSkipInventory(e.target.checked)}
						color="primary"
					/>
				}
				label="Skip Inventory?"
			/>
			<FormControlLabel
				sx={{ ml: 1 }}
				control={
					<Checkbox
						disabled={!!historySelected}
						checked={!!onOrderOnly}
						onChange={(e) => safeSetOnOrderOnly(e.target.checked)}
						color="primary"
					/>
				}
				label="Grab only On Order?"
			/>
		</>
	);
};

const JobSettings = ({ jobSettings, onSave, onRun, tipContext, client, toggleJob, posId, reloadLogs }) => {
	const hasExistingJob = jobSettings != null && jobSettings.id != null && String(jobSettings.id).trim() !== '';
	const [openMore, setOpenMore] = useState(false);
	const moreElement = useRef(null);

	const [runDate, setRunDate] = useState(moment());
	const [sinceLastRun, setSinceLastRun] = useState(false);
	const [historySelected, setHistorySelected] = useState(false);
	const [loadFullInventory, setLoadFullInventory] = useState(false);
	const [skipInventory, setSkipInventory] = useState(posId === 1200); // default on for Shopify
	const [onOrderOnly, setOnOrderOnly] = useState(false);
	const [grabAllOnOrder, setGrabAllOnOrder] = useState(false);
	const [monthsBack, setMonthsBack] = useState(1); // of months back to grab history
	const [historySkipInventory, setHistorySkipInventory] = useState(posId === 1200); // Shopify and Heartland; default on for Shopify, off for Heartland
	const [jobData, setJobData] = useState({ ...(jobSettings ?? {}), time_of_day: normalizeTimeOfDay(jobSettings?.time_of_day) });

	const [mergeOpen, setMergeOpen] = useState(false);
	const [mergeRunning, setMergeRunning] = useState(false);
	const [mergeStartMonth, setMergeStartMonth] = useState(moment().subtract(1, 'month').startOf('month'));
	const [mergeEndMonth, setMergeEndMonth] = useState(moment().subtract(1, 'month').startOf('month'));

	const [lsxStatusesAvailable, setLsxStatusesAvailable] = useState([]);
	const [lsxStatusesSelected, setLsxStatusesSelected] = useState([]);
	const [lsxStatusesSaving, setLsxStatusesSaving] = useState(false);

	useEffect(() => {
		setJobData({ ...(jobSettings || {}), time_of_day: normalizeTimeOfDay(jobSettings?.time_of_day) });
	}, [jobSettings]);

	useEffect(() => {
		if (!(runDate.month() === moment().month() && runDate.year() === moment().year())) {
			setSinceLastRun(false);
			setLoadFullInventory(false);
			setSkipInventory(false);
			setOnOrderOnly(false);
			setGrabAllOnOrder(false);
			setHistorySelected(true);
		} else {
			setHistorySelected(false);
		}
	}, [runDate]);

	useEffect(() => {
		// when client/pos changes, reset defaults
		setSkipInventory(posId === 1200);
		setHistorySkipInventory(posId === 1200);
	}, [posId]);

	useEffect(() => {
		if (posId !== 1201 || !client?.Id) {
			setLsxStatusesAvailable([]);
			setLsxStatusesSelected([]);
			return;
		}
		let cancelled = false;
		FlightService.getLightspeedXSeriesSalesStatuses(client.Id)
			.then((response) => {
				if (cancelled) return;
				const data = response?.data?.data ?? response?.data ?? response ?? {};
				setLsxStatusesAvailable(Array.isArray(data.available) ? data.available : []);
				setLsxStatusesSelected(Array.isArray(data.selected) ? data.selected : []);
			})
			.catch((err) => {
				console.error('LSX sales-statuses load failed', err);
			});
		return () => { cancelled = true; };
	}, [posId, client?.Id]);

	const handleLsxStatusesChange = async (next) => {
		const valueArray = typeof next === 'string' ? next.split(',') : next;
		setLsxStatusesSelected(valueArray);
		try {
			setLsxStatusesSaving(true);
			await FlightService.saveLightspeedXSeriesSalesStatusSelection(client.Id, valueArray);
			tipContext.showSuccess('Sales statuses saved');
		} catch (err) {
			console.error(err);
			tipContext.show(err.response?.data?.message || 'Error saving sales statuses');
		} finally {
			setLsxStatusesSaving(false);
		}
	};

	const syncFromStagingToActuals = async () => {
		tipContext.show('Not supported yet.');
		return;
		setOpenMore(false);
		tipContext.showSuccess('Syncing from Staging to Actuals. This can take 30-60 seconds.');
		FlightService.syncFromStagingToActuals(client.Id).then((response) => {
			tipContext.showSuccess('Synced from Staging to Actuals');
		}).catch((error) => {
			console.log(error);
			tipContext.show('Error syncing from Staging to Actuals');
		});
	};

	const runHeartlandMerge = async () => {
		if (!mergeStartMonth || !mergeEndMonth) {
			tipContext.show('Please select a start and end month');
			return;
		}
		const startMonth = mergeStartMonth.format('YYYY-MM');
		const endMonth = mergeEndMonth.format('YYYY-MM');
		if (startMonth > endMonth) {
			tipContext.show('Start month must be on or before end month');
			return;
		}

		setMergeRunning(true);
		try {
			tipContext.showSuccess('Merging actuals. This may take several minutes for larger ranges...', 10);
			await FlightService.mergeHeartlandActuals(client.Id, startMonth, endMonth);
			tipContext.showSuccess('Merge complete.', 10);
			setMergeOpen(false);
		} catch (error) {
			console.log(error);
			tipContext.show(error.response?.data?.message || 'Error merging actuals');
		} finally {
			setMergeRunning(false);
		}
	}

	const startHistoryLoad = async () => {
		if (monthsBack === '') {
			tipContext.show('Please select a number of months back');
			return;
		}
		try {
			// The backend runs the full history load synchronously within this request,
			// which can legitimately take longer than a short timeout for larger ranges.
			// Just await the real request instead of racing it against an artificial
			// timeout, so we never show a false "error" while the load is still running.
			tipContext.showSuccess('Starting history load. This may take a while for larger date ranges...', 8);

			await FlightService.loadHistory(client.Id, monthsBack, { skipInventory: (posId === 1200 || posId === 1202) ? historySkipInventory : undefined });

			reloadLogs();
			if (posId === 1202) {
				// Heartland history is queued one month per flight; each month shows in the logs as it runs
				tipContext.showSuccess('History months queued. Each month appears in the logs as it runs.', 15);
			} else {
				tipContext.showSuccess('History grab complete. Logs have been refreshed.', 15);
			}
		} catch (error) {
			console.log(error);
			tipContext.show(error.response?.data?.message || 'Error loading history');
		}			
	}

	const openDocumentation = () => {
		// confirm that user wants to open a new tab with doc
		if (window.confirm('Are you sure you want to open the documentation?')) {
			window.open('https://managementone.atlassian.net/wiki/x/BID4Rw', '_blank');
		}
	}

	return (
		<Paper elevation={2} sx={{mb:2}}>
			<Box sx={{mb:2}} spacing={2}>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<CardHeader
						title={'Flight Control Logs for ' + (client.ClientCode ?? '')}
						sx={{
							backgroundColor: 'white',
							'& .MuiTypography-root': {
									fontSize: '16px',
							},
							flexGrow: 1,
							borderBottom: '1px solid #ccc',
							fontWeight: 'bold',
						}}
					/>
					{hasExistingJob ? (
						<>
							<IconButton onClick={openDocumentation}>
								<HelpOutlineIcon />
							</IconButton>
							<IconButton onClick={() => { setOpenMore(true) }} ref={moreElement}>
								<MoreVertOutlined sx={{
									color: 'black', 
								}} />
							</IconButton>
							<Menu
								open={openMore}
								anchorEl={moreElement.current}
								onClose={() => { setOpenMore(false) }}
							>
								<MenuItem dense onClick={() => syncFromStagingToActuals()}>Sync from Staging</MenuItem>
							</Menu>
						</>
					) : null}
				</div>
				{hasExistingJob ?
					<>
						<Box sx={{ mx: 2, mb: 2 }}>
							<Stack sx={{mb: 2}}>
								{(posId === 1200) ? (
									<ShopifyPilotJobSettings
										historySelected={historySelected}
										options={{ skipInventory: !!skipInventory, loadFullInventory: !!loadFullInventory }}
										onChange={(next) => {
											setSkipInventory(!!next.skipInventory);
											setLoadFullInventory(!!next.loadFullInventory);
										}}
										client={client}
										tipContext={tipContext}
									/>
								) : null}
								{(posId === 1210) ? (
									<LightSpeedRJobSettings
										historySelected={historySelected}
										options={{ skipInventory: !!skipInventory, loadFullInventory: !!loadFullInventory, sinceLastRun: !!sinceLastRun, grabAllOnOrder: !!grabAllOnOrder }}
									onChange={(next) => {
										setSkipInventory(!!next.skipInventory);
										setLoadFullInventory(!!next.loadFullInventory);
										setSinceLastRun(!!next.sinceLastRun);
											setGrabAllOnOrder(!!next.grabAllOnOrder);
										}}
										client={client}
										tipContext={tipContext}
									/>
								) : null}
								{(posId === 1113 || posId === 1201 || posId === 1202 || posId === 1203) ? (
									<LightspeedRunOptions
										historySelected={historySelected}
										sinceLastRun={sinceLastRun}
										setSinceLastRun={setSinceLastRun}
										loadFullInventory={loadFullInventory}
										setLoadFullInventory={setLoadFullInventory}
										skipInventory={skipInventory}
										setSkipInventory={setSkipInventory}
										onOrderOnly={onOrderOnly}
										setOnOrderOnly={setOnOrderOnly}
									/>
								): null}
								{posId === 1201 && lsxStatusesAvailable.length > 0 ? (
									<FormControl sx={{ mt: 1, mb: 1, width: 300 }} size="small">
										<InputLabel id="lsx-statuses-label">Sales statuses to include</InputLabel>
										<Select
											labelId="lsx-statuses-label"
											multiple
											value={lsxStatusesSelected}
											onChange={(e) => handleLsxStatusesChange(e.target.value)}
											input={<OutlinedInput label="Sales statuses to include" />}
											disabled={lsxStatusesSaving}
											renderValue={(selected) => (
												<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
													{selected.map((v) => <Chip key={v} label={v} size="small" />)}
												</div>
											)}
										>
											{lsxStatusesAvailable.map((s) => (
												<MenuItem key={s} value={s}>
													<Checkbox checked={lsxStatusesSelected.indexOf(s) > -1} />
													<ListItemText primary={s} />
												</MenuItem>
											))}
										</Select>
									</FormControl>
								) : null}
							</Stack>
							<LocalizationProvider dateAdapter={AdapterMoment}>
								<DatePicker
									views={['year', 'month']}
									label="Run Date"
									minDate={moment('2000-01-01')}
									maxDate={moment('2100-01-01')}
									value={runDate}
									onChange={(newValue) => {
										setRunDate(newValue);
										if (newValue.month() !== moment().month()) {
											setSinceLastRun(false);
										}
									}}
									renderInput={(params) => <TextField {...params} />}
									inputFormat="MMM YYYY"
								/>
							</LocalizationProvider>
							<Button
								variant="contained"
								color="primary"
								sx={{ m: 1 }}
								onClick={() => (typeof onRun === 'function' ? onRun({
									period: runDate,
									sinceLastRun: sinceLastRun,
									loadFullInventory: loadFullInventory,
									skipInventory: skipInventory,
									onOrderOnly: onOrderOnly,
									grabAllOnOrder: grabAllOnOrder,
								}) : undefined)}
							>
								Run
							</Button>
							{posId === 1202 ? (
								<Button
									variant="outlined"
									color="primary"
									sx={{ m: 1 }}
									disabled={mergeRunning}
									onClick={() => setMergeOpen(true)}
								>
									Merge Actuals
								</Button>
							) : null}
						</Box>
						{posId === 1202 ? (
							<Dialog open={mergeOpen} onClose={() => (mergeRunning ? null : setMergeOpen(false))} maxWidth="xs" fullWidth>
								<DialogTitle>Merge Actuals</DialogTitle>
								<DialogContent>
									<Typography variant="body2" sx={{ mb: 2 }}>
										Flights load raw data only for past months. Pick the months to merge into actuals. This may take a few minutes per month.
									</Typography>
									<LocalizationProvider dateAdapter={AdapterMoment}>
										<Stack direction="row" spacing={2} sx={{ mt: 1 }}>
											<DatePicker
												views={['year', 'month']}
												label="Start Month"
												minDate={moment('2000-01-01')}
												maxDate={moment()}
												value={mergeStartMonth}
												onChange={(v) => setMergeStartMonth(v)}
												renderInput={(params) => <TextField {...params} fullWidth />}
												inputFormat="MMM YYYY"
											/>
											<DatePicker
												views={['year', 'month']}
												label="End Month"
												minDate={moment('2000-01-01')}
												maxDate={moment()}
												value={mergeEndMonth}
												onChange={(v) => setMergeEndMonth(v)}
												renderInput={(params) => <TextField {...params} fullWidth />}
												inputFormat="MMM YYYY"
											/>
										</Stack>
									</LocalizationProvider>
								</DialogContent>
								<DialogActions>
									<Button onClick={() => setMergeOpen(false)} disabled={mergeRunning}>Cancel</Button>
									<Button variant="contained" onClick={() => runHeartlandMerge()} disabled={mergeRunning}>
										{mergeRunning ? 'Merging...' : 'Merge'}
									</Button>
								</DialogActions>
							</Dialog>
						) : null}
						{client.PointOfSaleId === 1113 || client.PointOfSaleId === 1200 || client.PointOfSaleId === 1201 || client.PointOfSaleId === 1202 || client.PointOfSaleId === 1203 || client.PointOfSaleId === 1210 ?
							<>
								<Box
									sx={{
										px: 2,
										// py: 2,
										borderTop: '1px solid #ccc',
										fontWeight: 'normal',
									}}
								>
									<h3>
										Run History
									</h3>
								</Box>
								<Box
									sx={{ 
										px: 2,
										pt: 2,
										borderTop: '1px solid #ccc',
									}}
								>
									{(client.PointOfSaleId === 1200 || client.PointOfSaleId === 1202) ? (
										<FormControlLabel
											sx={{ ml: 1, mb: 1 }}
											control={
												<Checkbox
													checked={historySkipInventory}
													onChange={(e) => setHistorySkipInventory(e.target.checked)}
													color="primary"
												/>
											}
											label="Skip Inventory during history load?"
										/>
									) : null}
									<TextField
										label="Months Back"
										select
										value={monthsBack}
										onChange={(e) => setMonthsBack(e.target.value)}
										SelectProps={{
											native: true,
										}}
										sx={{ width: 300 }}
									>
										<option value=""></option>
										{Array.from({ length: 24 }, (_, index) => (
											<option key={index + 1} value={index + 1}>
												{index + 1}
											</option>
										))}
									</TextField>
									<Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => startHistoryLoad()}>Get History</Button>
								</Box>
							</>
						: null}
						<Box
							sx={{
								mt: 2,
								px: 2,
								// py: 2,
								borderTop: '1px solid #ccc',
								fontWeight: 'normal',
							}}
						>
							<h3>
								Daily Run Settings
							</h3>
						</Box>
						<Box
							sx={{ 
								px: 2,
								pt: 2,
								borderTop: '1px solid #ccc',
							}}
						>
							<TextField
								label="Time Zone"
								select
								value={jobData?.time_zone || ''}
								onChange={(e) => setJobData({...(jobData || {}), 'time_zone': e.target.value})}
								SelectProps={{
									native: true,
								}}
								sx={{ width: 300 }}
							>
								<option value=""></option>
								{timezoneOptions.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</TextField>
							<Typography variant="body2" sx={{ ml: 1 }}>Schedule Time of Day for Client in CST. Leave empty to run with at normal 3:00AM CST with all other clients.</Typography>
							{(posId == 1113 || posId == 1201) ?
								<>
									<Select
										label="Run Time (CST)"
										value={jobData.time_of_day}
										onChange={(e) => setJobData({...jobData, 'time_of_day': e.target.value})}
										sx={{ width: 300 }}
									>
										<MenuItem value={null}></MenuItem>
										<MenuItem value="01:00:00">1:00am</MenuItem>
										<MenuItem value="02:00:00">2:00am</MenuItem>
										<MenuItem value="04:00:00">4:00am</MenuItem>
										<MenuItem value="05:00:00">5:00am</MenuItem>
									</Select>
								</>
							: null}
						</Box>
						<Box id="action-buttons" sx={{ m: 2 }}>
							<Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => onSave(jobData || {})}>Save Setings</Button>
							{jobSettings?.enabled ?
								<Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => toggleJob(false)}>Disable Daily Run</Button> :
								<Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => toggleJob(true)}>Enable Daily Run</Button>
							}
						</Box>
					</>
				: <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => toggleJob(true)}>Create Job Settings</Button>}
			</Box>
		</Paper>
	);
}

export default JobSettings;

